import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  CalendarDays, Plus, Share2, Trash2, Upload, Users, Check, Copy, Pencil, X,
  Archive, ArchiveRestore, PauseCircle, PlayCircle, Repeat,
} from 'lucide-react';
import { toast } from 'sonner';
import { RecurrenceFields } from '../components/events/RecurrenceFields';
import { OccurrencePicker } from '../components/events/OccurrencePicker';
import { EventCard, StatusBadge } from '../components/events/EventCard';
import {
  blankRecurrence, recurrenceFromEvent, recurrenceToPayload, formatEventDate, timeLabel,
} from '../lib/recurrence';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIVE = ['upcoming', 'paused'];

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [occurrences, setOccurrences] = useState([]);
  const [occurrenceDate, setOccurrenceDate] = useState(null);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const fileInputRef = useRef(null);

  const blankForm = {
    name: '', description: '',
    event_date: '',
    is_all_day: true,
    start_time: '', end_time: '',
    poster_url: '',
    recurrence: blankRecurrence(),
  };
  const [form, setForm] = useState(blankForm);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/events`);
      setEvents(r.data || []);
    } catch (e) {
      toast.error('Failed to load events', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const upcoming = useMemo(() => events.filter((e) => LIVE.includes(e.status)), [events]);
  const past = useMemo(() => events.filter((e) => !LIVE.includes(e.status)), [events]);
  const visible = tab === 'upcoming' ? upcoming : past;

  const loadRegistrations = useCallback(async (eventId, date) => {
    try {
      const params = date ? { occurrence_date: date } : {};
      const r = await axios.get(`${API}/events/${eventId}/registrations`, { params });
      setRegistrations(r.data || []);
    } catch (e) {
      toast.error('Failed to load registrations', { description: e?.response?.data?.detail });
    }
  }, []);

  const loadOccurrences = useCallback(async (ev) => {
    if (!ev.recurrence) { setOccurrences([]); return null; }
    try {
      const r = await axios.get(`${API}/events/${ev.id}/occurrences`);
      const list = r.data?.occurrences || [];
      setOccurrences(list);
      return r.data?.next_date || list.find((o) => !o.is_past)?.date || list[0]?.date || null;
    } catch (e) {
      toast.error('Failed to load dates', { description: e?.response?.data?.detail });
      return null;
    }
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(blankForm);
    setDialogOpen(true);
  };
  const openEdit = (ev) => {
    setEditingId(ev.id);
    setForm({
      name: ev.name || '',
      description: ev.description || '',
      event_date: ev.event_date || '',
      is_all_day: !!ev.is_all_day,
      start_time: ev.start_time || '',
      end_time: ev.end_time || '',
      poster_url: ev.poster_url || '',
      recurrence: recurrenceFromEvent(ev),
    });
    setDialogOpen(true);
  };

  const submitEvent = async () => {
    if (!form.name.trim()) { toast.error('Event name is required'); return; }
    if (!form.event_date) { toast.error('Date is required'); return; }
    if (!form.is_all_day && !form.start_time) { toast.error('Start time required when not all-day'); return; }
    if (form.recurrence.type === 'weekly' && form.recurrence.weekdays.length === 0) {
      toast.error('Pick at least one weekday'); return;
    }
    const payload = { ...form, recurrence: recurrenceToPayload(form.recurrence) };
    try {
      if (editingId) {
        await axios.put(`${API}/events/${editingId}`, payload);
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/events`, payload);
        toast.success('Event created');
      }
      setDialogOpen(false);
      await loadEvents();
    } catch (e) {
      toast.error('Failed to save', { description: e?.response?.data?.detail || e?.message });
    }
  };

  const patchEvent = async (ev, patch, msg) => {
    try {
      const r = await axios.put(`${API}/events/${ev.id}`, patch);
      toast.success(msg);
      setSelectedEvent(r.data);
      await loadEvents();
    } catch (e) {
      toast.error('Update failed', { description: e?.response?.data?.detail || e?.message });
    }
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.name}" and all its registrations? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/events/${ev.id}`);
      toast.success('Event deleted');
      setSelectedEvent(null);
      await loadEvents();
    } catch (e) {
      toast.error('Delete failed', { description: e?.response?.data?.detail });
    }
  };

  const openDetail = async (ev) => {
    setSelectedEvent(ev);
    setRegistrations([]);
    const date = await loadOccurrences(ev);
    setOccurrenceDate(date);
    await loadRegistrations(ev.id, ev.recurrence ? date : null);
  };

  const pickOccurrence = async (date) => {
    setOccurrenceDate(date);
    await loadRegistrations(selectedEvent.id, date);
  };

  const copyShareLink = (ev) => {
    const url = `${window.location.origin}/event/${ev.share_code}`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success('Share link copied', { description: url }),
      () => toast.error('Copy failed — copy manually: ' + url),
    );
  };

  const shareEvent = async (ev) => {
    const url = `${window.location.origin}/event/${ev.share_code}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: ev.name, text: `Register for ${ev.name}`, url });
      } catch (_) { /* user cancelled */ }
    } else {
      copyShareLink(ev);
    }
  };

  const uploadPoster = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Poster too large (max 5 MB)'); return; }
    setUploadingPoster(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/uploads/event-poster`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, poster_url: r.data.poster_url }));
      toast.success('Poster uploaded');
    } catch (e) {
      toast.error('Upload failed', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setUploadingPoster(false);
    }
  };

  const toggleCheckIn = async (reg) => {
    try {
      const path = reg.is_checked_in ? 'undo-checkin' : 'checkin';
      await axios.post(`${API}/events/${selectedEvent.id}/registrations/${reg.id}/${path}`);
      await loadRegistrations(selectedEvent.id, selectedEvent.recurrence ? occurrenceDate : null);
      if (selectedEvent.recurrence) loadOccurrences(selectedEvent);
    } catch (e) {
      toast.error('Check-in failed', { description: e?.response?.data?.detail });
    }
  };

  const isRecurring = !!selectedEvent?.recurrence;
  const isLive = selectedEvent && LIVE.includes(selectedEvent.status);

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-6xl mx-auto pb-32" data-testid="events-page">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1
              className="text-3xl lg:text-5xl font-extrabold text-white"
              style={{ fontFamily: 'Outfit' }}
            >
              Events
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Promote and run venue events — guests register through your shareable link.
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-[#FF007F] hover:bg-[#cc0066] text-white font-bold tracking-wider rounded-full px-6"
            data-testid="events-create-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> NEW EVENT
          </Button>
        </div>

        <div className="inline-flex bg-white/5 border border-white/10 rounded-full p-1 mb-8" data-testid="events-tabs">
          {[
            { key: 'upcoming', label: 'Upcoming', count: upcoming.length },
            { key: 'past', label: 'Past', count: past.length },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                tab === t.key ? 'bg-[#FF007F] text-white' : 'text-gray-400 hover:text-white'
              }`}
              data-testid={`events-tab-${t.key}`}
            >
              {t.label} <span className={tab === t.key ? 'text-white/70' : 'text-gray-600'}>· {t.count}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : visible.length === 0 ? (
          <div
            className="bg-white/[0.03] border border-white/10 rounded-3xl p-12 text-center"
            data-testid="events-empty-state"
          >
            <CalendarDays className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            {tab === 'upcoming' ? (
              <>
                <p className="text-white font-bold text-lg mb-1">No upcoming events</p>
                <p className="text-gray-500 text-sm mb-6">Create an event to start collecting registrations.</p>
                <Button onClick={openCreate} className="bg-[#FF007F] hover:bg-[#cc0066] text-white rounded-full">
                  <Plus className="w-4 h-4 mr-2" /> CREATE EVENT
                </Button>
              </>
            ) : (
              <>
                <p className="text-white font-bold text-lg mb-1">No past events</p>
                <p className="text-gray-500 text-sm">Ended and archived events show up here.</p>
              </>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`events-grid-${tab}`}>
            {visible.map((ev) => (
              <EventCard key={ev.id} ev={ev} onClick={() => openDetail(ev)} />
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0a0a0a] border border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="event-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Outfit' }}>
              {editingId ? 'Edit event' : 'New event'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-wider">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Bollywood Night"
                className="bg-black/40 border-white/10 text-white"
                data-testid="event-form-name"
              />
            </div>
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-wider">Description (optional)</Label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="DJ Aman spinning Bollywood retro & hip-hop. Cover ₹500."
                rows={3}
                className="bg-black/40 border border-white/10 text-white rounded-md w-full px-3 py-2 text-sm placeholder:text-gray-600"
                data-testid="event-form-description"
              />
            </div>
            <RecurrenceFields
              value={form.recurrence}
              onChange={(recurrence) => setForm({ ...form, recurrence })}
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-xs uppercase tracking-wider">
                  {form.recurrence.type === 'none' ? 'Date' : 'Starts on'}
                </Label>
                <Input
                  type="date"
                  value={form.event_date}
                  onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  className="bg-black/40 border-white/10 text-white"
                  data-testid="event-form-date"
                />
              </div>
              <div className="flex items-end gap-2 pb-2">
                <Switch
                  checked={form.is_all_day}
                  onCheckedChange={(v) => setForm({ ...form, is_all_day: v })}
                  data-testid="event-form-all-day"
                />
                <Label className="text-gray-300 text-xs uppercase tracking-wider">All day</Label>
              </div>
            </div>
            {!form.is_all_day && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-300 text-xs uppercase tracking-wider">Start</Label>
                  <Input
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid="event-form-start"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 text-xs uppercase tracking-wider">End (optional)</Label>
                  <Input
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    className="bg-black/40 border-white/10 text-white"
                    data-testid="event-form-end"
                  />
                </div>
              </div>
            )}
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-wider">Poster</Label>
              {form.poster_url ? (
                <div className="relative mt-2">
                  <img src={form.poster_url} alt="Poster preview" className="w-full max-h-48 object-cover rounded-lg border border-white/10" />
                  <Button
                    type="button"
                    onClick={() => setForm({ ...form, poster_url: '' })}
                    variant="ghost"
                    className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full w-8 h-8 p-0"
                    data-testid="event-form-poster-remove"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full border border-dashed border-white/20 hover:border-[#FF007F] text-gray-400 hover:text-white rounded-lg py-8"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPoster}
                  data-testid="event-form-poster-upload"
                >
                  <Upload className="w-4 h-4 mr-2" /> {uploadingPoster ? 'Uploading…' : 'Upload poster'}
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { uploadPoster(e.target.files?.[0]); e.target.value = ''; }}
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-6">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-400">CANCEL</Button>
            <Button
              onClick={submitEvent}
              className="bg-[#FF007F] hover:bg-[#cc0066] text-white font-bold rounded-full px-6"
              data-testid="event-form-submit"
            >
              {editingId ? 'SAVE' : 'CREATE'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={(o) => !o && setSelectedEvent(null)}>
        <DialogContent className="bg-[#0a0a0a] border border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="event-detail-dialog">
          {selectedEvent && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white text-2xl flex items-center gap-3 flex-wrap" style={{ fontFamily: 'Outfit' }}>
                  {selectedEvent.name}
                  <StatusBadge status={selectedEvent.status} recurring={isRecurring} />
                </DialogTitle>
              </DialogHeader>
              {selectedEvent.poster_url && (
                <img
                  src={selectedEvent.poster_url}
                  alt={selectedEvent.name}
                  className="w-full max-h-64 object-cover rounded-xl border border-white/10 mb-4"
                />
              )}
              <p className="text-gray-300 text-sm mb-1" data-testid="event-detail-schedule">
                {isRecurring ? (
                  <>
                    <Repeat className="w-4 h-4 inline mr-1.5 text-[#d9a3ff]" />
                    {selectedEvent.recurrence_label}
                    {' · since '}{formatEventDate(selectedEvent.event_date, { day: 'numeric', month: 'short', year: 'numeric' })}
                  </>
                ) : (
                  <>
                    <CalendarDays className="w-4 h-4 inline mr-1.5 text-[#FF007F]" />
                    {formatEventDate(selectedEvent.event_date, { day: 'numeric', month: 'short', year: 'numeric' })}
                  </>
                )}
                {timeLabel(selectedEvent) ? ` · ${timeLabel(selectedEvent)}` : ''}
              </p>
              {selectedEvent.description && (
                <p className="text-gray-400 text-sm mb-4 whitespace-pre-line">{selectedEvent.description}</p>
              )}

              <div className="flex gap-2 flex-wrap mb-6">
                {isLive && selectedEvent.status !== 'paused' && (
                  <Button onClick={() => shareEvent(selectedEvent)} className="bg-[#00FFFF] hover:bg-[#00cccc] text-black font-bold rounded-full" data-testid="event-share-btn">
                    <Share2 className="w-4 h-4 mr-2" /> SHARE
                  </Button>
                )}
                <Button onClick={() => copyShareLink(selectedEvent)} variant="ghost" className="border border-white/10 text-gray-300 hover:text-white rounded-full" data-testid="event-copy-link">
                  <Copy className="w-4 h-4 mr-2" /> COPY LINK
                </Button>
                <Button onClick={() => { setSelectedEvent(null); openEdit(selectedEvent); }} variant="ghost" className="border border-white/10 text-gray-300 hover:text-white rounded-full" data-testid="event-edit-btn">
                  <Pencil className="w-4 h-4 mr-2" /> EDIT
                </Button>
                {isRecurring && selectedEvent.status !== 'archived' && (
                  selectedEvent.is_paused ? (
                    <Button onClick={() => patchEvent(selectedEvent, { is_paused: false }, 'Event resumed')} variant="ghost" className="border border-[#39FF14]/30 text-[#39FF14] hover:bg-[#39FF14]/10 rounded-full" data-testid="event-resume-btn">
                      <PlayCircle className="w-4 h-4 mr-2" /> RESUME
                    </Button>
                  ) : (
                    <Button onClick={() => patchEvent(selectedEvent, { is_paused: true }, 'Event paused — guests can no longer RSVP')} variant="ghost" className="border border-[#FFB800]/30 text-[#FFB800] hover:bg-[#FFB800]/10 rounded-full" data-testid="event-pause-btn">
                      <PauseCircle className="w-4 h-4 mr-2" /> PAUSE
                    </Button>
                  )
                )}
                {selectedEvent.status === 'archived' ? (
                  <Button onClick={() => patchEvent(selectedEvent, { is_archived: false }, 'Event restored')} variant="ghost" className="border border-white/10 text-gray-300 hover:text-white rounded-full" data-testid="event-unarchive-btn">
                    <ArchiveRestore className="w-4 h-4 mr-2" /> RESTORE
                  </Button>
                ) : (
                  <Button onClick={() => patchEvent(selectedEvent, { is_archived: true }, 'Event archived')} variant="ghost" className="border border-white/10 text-gray-300 hover:text-white rounded-full" data-testid="event-archive-btn">
                    <Archive className="w-4 h-4 mr-2" /> ARCHIVE
                  </Button>
                )}
                <Button onClick={() => deleteEvent(selectedEvent)} variant="ghost" className="border border-[#FF3B3B]/30 text-[#FF3B3B] hover:bg-[#FF3B3B]/10 rounded-full" data-testid="event-delete-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> DELETE
                </Button>
              </div>

              {isRecurring && (
                <div className="mb-5">
                  <p className="text-gray-400 text-xs uppercase tracking-wider font-bold mb-2">Pick a date</p>
                  {occurrences.length === 0 ? (
                    <p className="text-gray-500 text-sm">No dates yet.</p>
                  ) : (
                    <OccurrencePicker dates={occurrences} value={occurrenceDate} onChange={pickOccurrence} testIdPrefix="host-occurrence" />
                  )}
                </div>
              )}

              <h3 className="text-white font-bold uppercase tracking-wider text-sm mb-3 flex items-center gap-2" data-testid="event-registrations-heading">
                <Users className="w-4 h-4 text-[#00FFFF]" />
                Registrations ({registrations.length})
                {isRecurring && occurrenceDate && (
                  <span className="text-gray-500 normal-case tracking-normal font-normal">· {formatEventDate(occurrenceDate)}</span>
                )}
              </h3>
              {registrations.length === 0 ? (
                <p className="text-gray-500 text-sm py-6 text-center border border-white/5 rounded-lg">
                  {isRecurring ? 'No registrations for this date yet.' : 'No registrations yet. Share the link to start collecting RSVPs.'}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5">
                      <TableHead className="text-gray-400">Name</TableHead>
                      <TableHead className="text-gray-400">Phone</TableHead>
                      <TableHead className="text-gray-400">Party</TableHead>
                      <TableHead className="text-gray-400">Registered</TableHead>
                      <TableHead className="text-gray-400 text-right">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {registrations.map((r) => (
                      <TableRow key={r.id} className="border-white/5" data-testid={`event-reg-${r.id}`}>
                        <TableCell className="text-white">{r.guest_name}</TableCell>
                        <TableCell className="text-gray-300 font-mono text-xs">{r.guest_phone}</TableCell>
                        <TableCell className="text-gray-300 text-xs">{r.party_size || 1}</TableCell>
                        <TableCell className="text-gray-500 text-xs">{new Date(r.created_at).toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => toggleCheckIn(r)}
                            variant="ghost"
                            className={r.is_checked_in
                              ? 'bg-[#39FF14]/15 text-[#39FF14] border border-[#39FF14]/30 hover:bg-[#39FF14]/25'
                              : 'border border-white/10 text-gray-300 hover:text-white'}
                            data-testid={`event-reg-checkin-${r.id}`}
                          >
                            <Check className="w-3.5 h-3.5 mr-1" /> {r.is_checked_in ? 'CHECKED IN' : 'CHECK IN'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default EventsPage;
